import { createAsyncThunk } from '@reduxjs/toolkit';
import ContentPagesAPI from '@/api/ContentPagesAPI';
import ContentBlockSchemasAPI from '@/api/ContentBlockSchemasAPI';
import { IApiError } from '@/models/apiError.model';
import { CONTENT_PAGE_CREATE_ID, IBlogPostListQueryDTO, IBlogPostStorePayload } from '@/models/blog-post.model';
import { formatApiError } from '@/utils';
import { CONTENT_PAGES_SLICE_NAME } from './models';
import { RootState } from '@/store';
import { buildBlogPostStorePayload } from '../helpers/blogPostMapper';
import { contentPagesSelector } from './selectors';

const LIST_PAGE_SIZE = 10;

export const getContentPagesList = createAsyncThunk<
  Awaited<ReturnType<typeof ContentPagesAPI.list>>['data'],
  void,
  { serializedErrorType: IApiError; state: RootState }
>(
  `${CONTENT_PAGES_SLICE_NAME}/getList`,
  async (_, { getState }) => {
    const { list } = contentPagesSelector(getState());
    const params: IBlogPostListQueryDTO = {
      page: list.currentPage,
      per_page: LIST_PAGE_SIZE,
      sort: list.sortField,
      direction: list.sortDirection,
    };
    if (list.blogCategoryId) {
      params.blog_category_id = list.blogCategoryId;
    }
    const titleSearch = list.search.trim();
    if (titleSearch) {
      params.search = titleSearch;
    }
    const idSearch = list.searchId.trim();
    if (idSearch) {
      params.search_id = idSearch;
    }
    const authorSearch = list.searchAuthor.trim();
    if (authorSearch) {
      params.search_author = authorSearch;
    }
    const response = await ContentPagesAPI.list(params);
    return response.data;
  },
  { serializeError: formatApiError },
);

export const deleteContentPage = createAsyncThunk<string, { id: string }, { serializedErrorType: IApiError }>(
  `${CONTENT_PAGES_SLICE_NAME}/delete`,
  async ({ id }) => {
    await ContentPagesAPI.delete(id);
    return id;
  },
  { serializeError: formatApiError },
);

export const getContentBlockSchemas = createAsyncThunk<
  Awaited<ReturnType<typeof ContentBlockSchemasAPI.getSchemas>>['data'],
  void,
  { serializedErrorType: IApiError }
>(
  `${CONTENT_PAGES_SLICE_NAME}/getSchemas`,
  async () => {
    const response = await ContentBlockSchemasAPI.getSchemas();
    return response.data;
  },
  { serializeError: formatApiError },
);

export const getBlogCategories = createAsyncThunk<
  Awaited<ReturnType<typeof ContentPagesAPI.listBlogCategories>>['data'],
  void,
  { serializedErrorType: IApiError }
>(
  `${CONTENT_PAGES_SLICE_NAME}/getBlogCategories`,
  async () => {
    const response = await ContentPagesAPI.listBlogCategories();
    return response.data;
  },
  { serializeError: formatApiError },
);

export const getContentPageById = createAsyncThunk<
  Awaited<ReturnType<typeof ContentPagesAPI.getById>>['data'],
  { id: string },
  { serializedErrorType: IApiError }
>(
  `${CONTENT_PAGES_SLICE_NAME}/getById`,
  async ({ id }) => {
    const response = await ContentPagesAPI.getById(id);
    return response.data;
  },
  { serializeError: formatApiError },
);

export const revertContentPage = createAsyncThunk<
  Awaited<ReturnType<typeof ContentPagesAPI.revert>>['data'],
  { id: string },
  { serializedErrorType: IApiError }
>(
  `${CONTENT_PAGES_SLICE_NAME}/revert`,
  async ({ id }) => {
    const response = await ContentPagesAPI.revert(id);
    return response.data;
  },
  { serializeError: formatApiError },
);

export const setContentPagePublished = createAsyncThunk<
  { id: string; published: boolean },
  { id: string; published: boolean },
  { serializedErrorType: IApiError }
>(
  `${CONTENT_PAGES_SLICE_NAME}/setPublished`,
  async ({ id, published }) => {
    await ContentPagesAPI.setPublished(id, published);
    return { id, published };
  },
  { serializeError: formatApiError },
);

export const saveContentPage = createAsyncThunk<
  Awaited<ReturnType<typeof ContentPagesAPI.getById>>['data'] | { createdId: string; created: true },
  { id: string; payload: IBlogPostStorePayload },
  { serializedErrorType: IApiError }
>(
  `${CONTENT_PAGES_SLICE_NAME}/save`,
  async ({ id, payload }) => {
    if (id === CONTENT_PAGE_CREATE_ID) {
      const { data } = await ContentPagesAPI.create(payload);
      const postId = data.data.blog_post_id ?? data.data.id;
      await ContentPagesAPI.save(postId);
      return { createdId: postId, created: true };
    }
    await ContentPagesAPI.save(id);
    const response = await ContentPagesAPI.getById(id);
    return response.data;
  },
  { serializeError: formatApiError },
);

export const patchContentPage = createAsyncThunk<
  { payloadHash: string },
  { id: string; payload: IBlogPostStorePayload; payloadHash: string },
  { serializedErrorType: IApiError }
>(
  `${CONTENT_PAGES_SLICE_NAME}/patch`,
  async ({ id, payload, payloadHash }) => {
    await ContentPagesAPI.update(id, payload);
    return { payloadHash };
  },
  { serializeError: formatApiError },
);

export const triggerContentPageAutosave = createAsyncThunk<void, { id: string }, { state: RootState }>(
  `${CONTENT_PAGES_SLICE_NAME}/triggerAutosave`,
  async ({ id }, { getState, dispatch }) => {
    if (!id || id === CONTENT_PAGE_CREATE_ID) return;
    const state = contentPagesSelector(getState());
    const page = state.editor.page;
    if (!page) return;
    if (state.autosave.isPatching || !state.autosave.hasUnsavedChanges) return;
    const payload = buildBlogPostStorePayload(page);
    const payloadHash = JSON.stringify(payload);
    if (state.autosave.lastPatchedPayload === payloadHash) return;
    await dispatch(patchContentPage({ id, payload, payloadHash }));
  },
);
